import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PaymentApi, {
  OtrPaymentBlockedError,
  PaymentLinkMessagesDto,
  PaymentLinkOptionsDto,
  paymentLinkSendApi,
} from '../../api/PaymentApi';
import { createPaymentLinkSender, type CreatedLink } from '../../utils/paymentLinkSend';

/**
 * "Send payment link", straight after a case is created (Lloyd, Oct 2026: "it should have immediately
 * opened up an option for me to send the client via email and text a Stripe payment link").
 *
 * Prefilled with the plan's price, editable. Email and text are ticked by default only for a client
 * who pays the firm directly (see payments/paymentStepDefaults on the server); a referral opens with
 * nothing ticked and says why, and OTR offers no link at all. Text is only offered when the client has
 * a mobile number and has agreed to be texted.
 *
 * Nothing is sent until Send is pressed, and the exact words are on screen when it is. Send creates
 * the Stripe link (it is then on the case's Payments tab like any other) and sends it. Skip creates
 * nothing. A retry after a failed send sends the link already made (see utils/paymentLinkSend).
 */
const PaymentLinkStep: React.FC<{
  ticketGuid: string;
  /** Called after Send or Skip; the caller opens the case. */
  onDone: () => void;
  /**
   * When Create case's one press made a link but it did not all go (utils/paymentOnCreate): that
   * link, what went wrong, and which channels are still to send. Send then sends this same link.
   */
  attention?: {
    pendingLink: CreatedLink | null;
    problem: string;
    channels: { email: boolean; sms: boolean } | null;
  };
}> = ({ ticketGuid, onDone, attention }) => {
  const [options, setOptions] = React.useState<PaymentLinkOptionsDto | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [amount, setAmount] = React.useState('');
  const [email, setEmail] = React.useState(false);
  const [sms, setSms] = React.useState(false);
  const [preview, setPreview] = React.useState<PaymentLinkMessagesDto | null>(null);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [outcome, setOutcome] = React.useState<{ sent: boolean; reason: string } | null>(null);
  const sender = React.useRef(
    createPaymentLinkSender(paymentLinkSendApi, attention?.pendingLink ?? null),
  ).current;
  /** The link a failed try made, so the screen can say what a retry at another amount will do. */
  const [pendingCents, setPendingCents] = React.useState<number | null>(
    attention?.pendingLink?.TicketPayment_AmountCents ?? null,
  );

  React.useEffect(() => {
    PaymentApi.getPaymentLinkOptions(ticketGuid)
      .then((result) => {
        setOptions(result);
        // After a one press that did not all go, tick what is still to send, if a channel went.
        setEmail(attention?.channels ? attention.channels.email && !result.emailUnavailable : result.email);
        setSms(attention?.channels ? attention.channels.sms && !result.smsUnavailable : result.sms);
        const startCents = attention?.pendingLink?.TicketPayment_AmountCents ?? result.planCents;
        if (startCents) setAmount((startCents / 100).toFixed(2));
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Could not load the payment options'));
    // Read once: `attention` is fixed for the life of the step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketGuid]);

  const dollars = Number(amount);
  const amountValid = Number.isFinite(dollars) && dollars >= 0.5 && dollars <= 50000;
  const amountCents = Math.round(dollars * 100);

  // The words follow the amount, so what staff read is what the client gets.
  React.useEffect(() => {
    if (!amountValid || options?.mode === 'blocked') {
      setPreview(null);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      PaymentApi.previewPaymentLink(ticketGuid, amountCents)
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [ticketGuid, amountCents, amountValid, options?.mode]);

  const send = async () => {
    if (!amountValid || (!email && !sms)) return;
    setSending(true);
    setError(null);
    try {
      const result = await sender.send({
        ticketGuid,
        amountCents,
        note: options?.plan ?? undefined,
        channels: { email, sms },
      });
      setOutcome({ sent: result.sent, reason: result.reason });
    } catch (err) {
      const message =
        err instanceof OtrPaymentBlockedError || err instanceof Error
          ? err.message
          : 'The payment link could not be sent.';
      setError(
        sender.pendingLink()
          ? `${message.replace(/\.$/, '')}. The link was made and is on the Payments tab; Send again sends that same link.`
          : message,
      );
    } finally {
      setPendingCents(sender.pendingLink()?.TicketPayment_AmountCents ?? null);
      setSending(false);
    }
  };

  if (loadError) {
    return (
      <Stack spacing={2}>
        <Alert severity="warning">
          The case was created. The payment options could not be loaded ({loadError}); create a link
          on the case&apos;s Payments tab instead.
        </Alert>
        <Box>
          <Button variant="contained" onClick={onDone}>
            Open the case
          </Button>
        </Box>
      </Stack>
    );
  }

  if (!options) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (outcome) {
    return (
      <Stack spacing={2}>
        <Alert severity={outcome.sent ? 'success' : 'warning'}>
          {outcome.reason} The link is on the case&apos;s Payments tab.
        </Alert>
        <Box>
          <Button variant="contained" onClick={onDone}>
            Open the case
          </Button>
        </Box>
      </Stack>
    );
  }

  if (options.mode === 'blocked') {
    return (
      <Stack spacing={2}>
        <Alert severity="success">Case created.</Alert>
        <Alert severity="info">{options.note}</Alert>
        <Box>
          <Button variant="contained" onClick={onDone}>
            Open the case
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Alert severity="success">Case created for {options.clientName || 'the client'}.</Alert>
      {attention && !error && <Alert severity="warning">{attention.problem}</Alert>}
      <Typography variant="h6">Send a payment link</Typography>
      {options.note && <Alert severity="warning">{options.note}</Alert>}

      <TextField
        label="Amount"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        error={amount !== '' && !amountValid}
        helperText={
          amount !== '' && !amountValid
            ? 'Enter an amount between $0.50 and $50,000.'
            : options.planCents
              ? `The price of ${options.plan}. Change it if the client owes something else.`
              : 'No price is on record for this plan; enter the amount.'
        }
        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        sx={{ maxWidth: 280 }}
        size="small"
      />
      {pendingCents !== null && amountValid && pendingCents !== amountCents && (
        <Alert severity="info">
          A ${(pendingCents / 100).toFixed(2)} link was made on the last try and not sent. Sending now
          cancels it and makes a new one for ${(amountCents / 100).toFixed(2)}.
        </Alert>
      )}

      <Box>
        <FormControlLabel
          control={<Checkbox checked={email} onChange={(event) => setEmail(event.target.checked)} />}
          disabled={Boolean(options.emailUnavailable)}
          label={`Email${options.emailAddress ? ` (${options.emailAddress})` : ''}`}
        />
        {options.emailUnavailable && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1 }}>
            {options.emailUnavailable}
          </Typography>
        )}
        <FormControlLabel
          control={<Checkbox checked={sms} onChange={(event) => setSms(event.target.checked)} />}
          disabled={Boolean(options.smsUnavailable)}
          label={`Text${options.phoneNumber ? ` (${options.phoneNumber})` : ''}`}
        />
        {options.smsUnavailable && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1 }}>
            {options.smsUnavailable}
          </Typography>
        )}
      </Box>

      {preview && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2">What the client will receive</Typography>
          {email && (
            <>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Email:</strong> {preview.email.subject}
              </Typography>
              <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, m: 0 }}>
                {preview.email.body}
              </Box>
            </>
          )}
          {sms && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Text:</strong> {preview.sms}
            </Typography>
          )}
          {!email && !sms && (
            <Typography variant="body2" color="text.secondary">
              Tick email or text to send.
            </Typography>
          )}
        </Paper>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          disabled={!amountValid || (!email && !sms) || sending}
          onClick={() => void send()}
          startIcon={sending ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {sending ? 'Sending…' : 'Send payment link'}
        </Button>
        <Button variant="text" onClick={onDone} disabled={sending}>
          Skip
        </Button>
      </Stack>
    </Stack>
  );
};

export default PaymentLinkStep;
